import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import GlobalParticles from '@/components/GlobalParticles';
import { TranslatedText } from '@/components/TranslatedText';
import { LanguageSelector } from '@/components/LanguageSelector';

const testimonials = [
  {
    quote: "honestly? i was skeptical at first. another 'secret club' promising the moon. but 6 months in, i quit my job. my wife still thinks i got lucky lol",
    initials: "J.M.",
    role: "Canada",
  },
  {
    quote: "no bs here. just real people doing real things. made more connections in 2 weeks than 3 years on linkedin",
    initials: "A.K.",
    role: "Brazil",
  },
  {
    quote: "my ex used to say i was 'too shy to succeed'. now i run 3 stores and she has no idea its me. feels good ngl",
    initials: "R.S.",
    role: "Australia",
  },
  {
    quote: "finally somewhere i dont have to pretend to be someone im not. just build stuff and make money. simple as that",
    initials: "L.T.",
    role: "Singapore",
  },
  {
    quote: "i evolved. started making money like i didn't even know i could. but one thing that keeps me awake at night is that this community helps each other... these guys helped me make my first million and still i have no clue who they are",
    initials: "M.D.",
    role: "USA",
  },
];

const About = () => {
  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <GlobalParticles />
      {/* Header */}
      <motion.header
        className="fixed top-0 left-0 right-0 z-50 p-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <Link to="/">
            <Button
              variant="ghost"
              className="text-gold hover:bg-gold/10 gap-2"
              data-hover
            >
              <ArrowLeft size={18} />
              <TranslatedText>Back</TranslatedText>
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="ghost" className="text-gold/70 hover:text-gold" />
            <Link to="/login">
              <Button
                variant="outline"
                className="border-gold/30 text-gold hover:bg-gold/10 hover:border-gold/50 backdrop-blur-sm bg-background/20 tracking-wider text-sm"
                data-hover
              >
                <TranslatedText>Members Login</TranslatedText>
              </Button>
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Who Are We Section */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            className="text-gold/60 tracking-[0.3em] uppercase text-sm mb-6 block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <TranslatedText>Who We Are</TranslatedText>
          </motion.span>
          <motion.h1
            className="text-4xl md:text-6xl lg:text-7xl font-serif mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <span className="text-foreground"><TranslatedText>The Architects of</TranslatedText></span>
            <br />
            <span className="gradient-text-gold text-glow"><TranslatedText>Invisible Empires</TranslatedText></span>
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            <TranslatedText>We are a collective of strategists, builders, and visionaries who've mastered the art of building wealth without exposure. No followers needed. No fame required. Just results that speak louder than any personal brand ever could.</TranslatedText>
          </motion.p>
          <motion.div
            className="mt-12 grid grid-cols-3 gap-8 max-w-md mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold gradient-text-gold">144</div>
              <div className="text-sm text-muted-foreground mt-1"><TranslatedText>Members</TranslatedText></div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold gradient-text-gold">12</div>
              <div className="text-sm text-muted-foreground mt-1"><TranslatedText>Countries</TranslatedText></div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold gradient-text-gold">∞</div>
              <div className="text-sm text-muted-foreground mt-1"><TranslatedText>Potential</TranslatedText></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-b from-background via-charcoal/30 to-background">
        <div className="max-w-4xl mx-auto">
          <motion.span
            className="text-gold/60 tracking-[0.3em] uppercase text-sm mb-6 block text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <TranslatedText>Our Mission</TranslatedText>
          </motion.span>
          <motion.h2
            className="text-3xl md:text-5xl lg:text-6xl font-serif text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <span className="text-foreground"><TranslatedText>Redefine</TranslatedText></span>{" "}
            <span className="gradient-text-gold text-glow"><TranslatedText>Success</TranslatedText></span>
          </motion.h2>
          <div className="space-y-8">
            {[
              {
                title: "Privacy is Power",
                description: "In an age of oversharing, anonymity is your greatest asset. We teach you to build empires that don't require your face.",
              },
              {
                title: "Systems Over Spotlight",
                description: "Create automated revenue streams that work while you sleep. Your business runs without you being the brand.",
              },
              {
                title: "Freedom Without Fame",
                description: "True wealth means having options. We help you build the financial foundation for complete life autonomy.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="flex gap-6 items-start p-6 rounded-lg glass-panel"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.2 }}
              >
                <div className="w-12 h-12 rounded-full border border-gold/30 flex items-center justify-center text-gold font-serif text-xl flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    <TranslatedText>{item.title}</TranslatedText>
                  </h3>
                  <p className="text-muted-foreground">
                    <TranslatedText>{item.description}</TranslatedText>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="min-h-screen flex items-center justify-center px-6 py-24">
        <div className="max-w-5xl mx-auto">
          <motion.span
            className="text-gold/60 tracking-[0.3em] uppercase text-sm mb-6 block text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <TranslatedText>Anonymous Voices</TranslatedText>
          </motion.span>
          <motion.h2
            className="text-3xl md:text-5xl font-serif text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <span className="text-foreground"><TranslatedText>From Those Who</TranslatedText></span>{" "}
            <span className="gradient-text-gold text-glow"><TranslatedText>Transformed</TranslatedText></span>
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                className={`p-8 rounded-lg glass-panel ${
                  i === 0 ? 'md:col-span-2' : ''
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
              >
                <p className="text-lg md:text-xl text-foreground/90 italic mb-6 leading-relaxed">
                  "<TranslatedText>{testimonial.quote}</TranslatedText>"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center">
                    <span className="text-gold text-sm font-medium">{testimonial.initials}</span>
                  </div>
                  <div>
                    <div className="text-foreground font-medium"><TranslatedText>Anonymous Member</TranslatedText></div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* CTA */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Link to="/apply">
              <Button variant="hero" size="xl" data-hover>
                <TranslatedText>Join the Club</TranslatedText>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
